import { Button, Input, toast } from "@gotts.ai/ui";
import { useState } from "react";

import { useRpcRequest } from "../../hooks/useRpcRequest";

import type { ChainControlSharedProps } from "./types";

export type MiningMode = "auto" | "manual" | "interval";

const MODE_DESCRIPTIONS: Record<MiningMode, string> = {
  auto: "Blocks mined instantly on transaction submission",
  manual: "Blocks must be mined manually via the Mine Block button",
  interval: "Blocks auto-mined every N seconds",
};

const INTERVAL_PRESETS = [
  { label: "1s", value: 1 },
  { label: "2s (L2)", value: 2 },
  { label: "12s (ETH)", value: 12 },
  { label: "60s", value: 60 },
];

interface MiningControlsProps extends ChainControlSharedProps {
  miningMode: MiningMode;
  onMiningModeChange: (mode: MiningMode) => void;
}

export function MiningControls({
  loading,
  withLoading,
  onAction,
  miningMode,
  onMiningModeChange,
}: MiningControlsProps) {
  const { request } = useRpcRequest();
  const [intervalSeconds, setIntervalSeconds] = useState(12);
  const [appliedInterval, setAppliedInterval] = useState(12);

  async function handleMiningMode(mode: MiningMode) {
    await withLoading("miningMode", async () => {
      if (mode === "auto") {
        await request("evm_setAutomine", [true]);
      } else if (mode === "manual") {
        await request("evm_setAutomine", [false]);
        toast.warning("Manual mode: transactions queue until you mine a block");
      } else {
        await request("evm_setAutomine", [false]);
        await request("evm_setIntervalMining", [intervalSeconds * 1000]);
        setAppliedInterval(intervalSeconds);
      }
      onMiningModeChange(mode);
      toast.success(`Mining mode set to ${mode}`);
      onAction?.(`Mining: ${mode}`);
    });
  }

  async function applyInterval(seconds: number) {
    setIntervalSeconds(seconds);
    if (miningMode === "interval") {
      await withLoading("miningMode", async () => {
        await request("evm_setAutomine", [false]);
        await request("evm_setIntervalMining", [seconds * 1000]);
        setAppliedInterval(seconds);
        toast.success(`Interval set to ${String(seconds)}s`);
        onAction?.(`Interval: ${String(seconds)}s`);
      });
    }
  }

  const intervalChanged =
    miningMode === "interval" && intervalSeconds !== appliedInterval;

  return (
    <div className="space-y-3">
      {/* Mode selector — pill-shaped segmented control */}
      <div className="flex rounded-full border border-[var(--glass-border)] bg-[rgba(255,255,255,0.02)] p-0.5">
        {(["auto", "manual", "interval"] as const).map((mode) => (
          <button
            key={mode}
            onClick={() => handleMiningMode(mode)}
            className={`flex-1 rounded-full px-4 py-2 text-xs font-medium capitalize transition-all ${
              miningMode === mode
                ? "bg-[var(--color-gold)]/15 text-[var(--color-gold)] shadow-[0_0_12px_rgba(201,168,76,0.1)]"
                : "text-[var(--color-text-muted)] hover:text-[var(--color-text-dim)]"
            }`}
            disabled={loading === "miningMode"}
          >
            {mode}
          </button>
        ))}
      </div>

      <p className="text-center text-[10px] text-[var(--color-text-muted)]">
        {MODE_DESCRIPTIONS[miningMode]}
      </p>

      {miningMode === "interval" && (
        <div className="space-y-3 rounded-sm border border-[var(--glass-border)] bg-[rgba(255,255,255,0.015)] p-3">
          {intervalChanged && (
            <p className="text-[10px] text-[var(--color-text-muted)]">
              Current: {appliedInterval}s
            </p>
          )}
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              value={String(intervalSeconds)}
              onChange={(e) => {
                setIntervalSeconds(
                  Math.max(1, parseInt(e.target.value, 10) || 1),
                );
              }}
              className="w-20"
            />
            <span className="flex-1 text-[10px] text-[var(--color-text-muted)]">
              seconds between blocks
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleMiningMode("interval")}
              loading={loading === "miningMode"}
              disabled={loading !== null || !intervalChanged}
            >
              Apply
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {INTERVAL_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`btn-percentage-pill ${
                  appliedInterval === p.value
                    ? "btn-percentage-pill-selected"
                    : ""
                }`}
                onClick={() => applyInterval(p.value)}
                disabled={loading !== null}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
